
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HeroSection from "@/components/home/HeroSection";
import HowItWorksSection from "@/components/home/HowItWorksSection";
import LatestReagentsSection from "@/components/home/LatestReagentsSection";
import TestimonialsSection from "@/components/home/TestimonialsSection";
import CTASection from "@/components/home/CTASection";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { fetchReagents } from "@/services/reagentService";
import { useAuth } from "@/contexts/auth";

const Index = () => {
  console.log("Index page rendering");
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Redirect authenticated users to the dashboard page
  useEffect(() => {
    if (user) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    // Check if we already have data in the cache
    const existingData = queryClient.getQueryData(['reagents']) || queryClient.getQueryData(['latestReagents']);
    
    if (!existingData) {
      // Only show loading toast if we don't have cached data
      toast.info("Welcome to ReagentShare! Loading latest reagents...");
    }
    
    const loadReagents = async () => {
      try {
        // Check if we're already loading the query
        const query = queryClient.getQueryState(['latestReagents']);
        if (query && (query.status === 'pending' || query.fetchStatus === 'fetching')) {
          console.log("Query is already in progress, skipping prefetch");
          return;
        }
        
        // Prefetch reagents directly to ensure data is loaded
        console.log("Prefetching reagents data from Index page...");
        const data = await fetchReagents();
        
        if (data && data.length > 0) {
          // Set the prefetched data in the query cache
          queryClient.setQueryData(['reagents'], data);
          queryClient.setQueryData(['latestReagents'], data);
          
          console.log("Index: Prefetched reagents:", data.length);
          toast.success(`Loaded ${data.length} reagents successfully`);
        } else {
          console.log("No reagents returned from fetchReagents");
          toast.info("No reagents found in the database");
        }
      } catch (error) {
        console.error("Error prefetching reagents:", error);
        toast.error("Error loading reagents: " + (error instanceof Error ? error.message : "Unknown error"));
      }
    };
    
    // Start loading reagents with a small delay to allow for component mounting
    const timer = setTimeout(() => {
      loadReagents();
    }, 500);
    
    return () => {
      clearTimeout(timer);
      console.log("Index page unmounting");
    };
  }, [queryClient]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main>
        <HeroSection />
        <HowItWorksSection />
        <LatestReagentsSection />
        <TestimonialsSection />
        <CTASection />
      </main>
      <Footer />
    </div>
  );
};

export default Index;
